import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";

const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const hashParams = new URLSearchParams(hash.replace("#", ""));
      const sessionId = hashParams.get("session_id");

      // Extract user_type from query params (passed from register page)
      const queryParams = new URLSearchParams(location.search);
      const userType = queryParams.get("user_type") || "patient";
      const roleExplicit = queryParams.get("role_explicit") === "1";

      if (!sessionId) {
        toast.error("Session invalide");
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ 
            session_id: sessionId,
            user_type: userType,
            role_explicit: roleExplicit
          })
        });

        if (!response.ok) {
          throw new Error("Échec de l'authentification");
        }

        const data = await response.json();
        
        // Store token and login
        localStorage.setItem("token", sessionId);
        login(data.user, sessionId);

        // First Google login without a chosen role → ask the user to pick one
        if (data.needs_role_selection) {
          navigate("/select-role", { replace: true });
          return;
        }

        toast.success(`Bienvenue, ${data.user.name} !`);
        
        // Redirect based on user type
        if (data.user.user_type === "nurse") {
          // Check if nurse profile needs onboarding
          if (data.needs_onboarding) {
            navigate("/nurse/onboarding", { replace: true, state: { user: data.user } });
          } else {
            navigate("/nurse/dashboard", { replace: true, state: { user: data.user } });
          }
        } else if (data.user.user_type === "admin") {
          navigate("/admin", { replace: true, state: { user: data.user } });
        } else {
          navigate("/dashboard", { replace: true, state: { user: data.user } });
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Erreur d'authentification");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, login, location.search]);

  return (
    <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center">
      <div className="text-center">
        <div className="spinner mx-auto mb-4" />
        <p className="text-white">Connexion en cours...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
